import type { CreateContractFormSchemaType } from "@/pages/shipper-contract-create/model/validation";
import { fromIso8601Date, toIso8601Date } from "@/shared/lib/datetime";
import { BaseAccordion } from "@/shared/ui/base-accordion";
import { BaseAccordionSummary } from "@/shared/ui/base-accordion-summary";
import { BaseDatePicker } from "@/shared/ui/base-date-picker";
import { BaseFormLabel } from "@/shared/ui/base-form-label";
import { BaseNumberField } from "@/shared/ui/base-number-field";
import { BaseTextField } from "@/shared/ui/base-text-field";
import { AccordionDetails, Box, Grid2, Stack } from "@mui/material";
import { type Control, Controller, useController } from "react-hook-form";

export const PaymentForm = ({
  control,
}: {
  control: Control<CreateContractFormSchemaType>;
}) => {
  const shippingFeeController = useController({
    name: "shippingFee",
    control,
  });
  const highwayFeeController = useController({
    name: "highwayFee",
    control,
  });
  return (
    <BaseAccordion>
      <BaseAccordionSummary>基本運賃</BaseAccordionSummary>
      <AccordionDetails>
        <Stack spacing={3}>
          <BaseFormLabel label="運賃" isRequired>
            <BaseNumberField
              {...shippingFeeController.field}
              size="small"
              startAdornment="¥"
              error={shippingFeeController.fieldState.invalid}
              helperText={shippingFeeController.fieldState.error?.message}
            />
          </BaseFormLabel>
          <Box display="flex" gap={3}>
            <BaseFormLabel label="有料道路利用料の別途支払い" isRequired>
              <BaseTextField
                {...highwayFeeController.field}
                size="small"
                value={highwayFeeController.field.value > 0 ? "あり" : "なし"}
                fullWidth
                error={highwayFeeController.fieldState.invalid}
                helperText={highwayFeeController.fieldState.error?.message}
              />
            </BaseFormLabel>
            <BaseFormLabel
              label="有料道路区間"
              sx={{ width: "100%", maxWidth: { md: "376px" } }}
            >
              <Controller
                name="highwaySection"
                control={control}
                render={({ field, fieldState }) => (
                  <BaseTextField
                    {...field}
                    size="small"
                    value={field.value}
                    fullWidth
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </BaseFormLabel>
          </Box>
          <Grid2 container spacing={3} width="100%">
            <Grid2 size={{ xs: 12, md: 3 }}>
              <BaseFormLabel label="燃料サーチャージ">
                <Controller
                  name="fuelSurcharge"
                  control={control}
                  render={({ field, fieldState }) => (
                    <BaseNumberField
                      {...field}
                      size="small"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      startAdornment="¥"
                    />
                  )}
                />
              </BaseFormLabel>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <BaseFormLabel label="荷役料金">
                <Controller
                  name="cargoHandlingFee"
                  control={control}
                  render={({ field, fieldState }) => (
                    <BaseNumberField
                      {...field}
                      size="small"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      startAdornment="¥"
                    />
                  )}
                />
              </BaseFormLabel>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <BaseFormLabel label="その他料金">
                <Controller
                  name="othersFee"
                  control={control}
                  render={({ field, fieldState }) => (
                    <BaseNumberField
                      {...field}
                      size="small"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      startAdornment="¥"
                    />
                  )}
                />
              </BaseFormLabel>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <BaseFormLabel label="調整金額">
                <Controller
                  name="adjustmentFee"
                  control={control}
                  render={({ field, fieldState }) => (
                    <BaseNumberField
                      {...field}
                      size="small"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      startAdornment="¥"
                    />
                  )}
                />
              </BaseFormLabel>
            </Grid2>
          </Grid2>
          <BaseFormLabel label="荷主からの入金予定日">
            <Controller
              name="paymentDate"
              control={control}
              render={({ field, fieldState }) => (
                <BaseDatePicker
                  {...field}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  value={field.value ? fromIso8601Date(field.value) : null}
                  onChange={(value) =>
                    field.onChange(value ? toIso8601Date(value) : null)
                  }
                />
              )}
            />
          </BaseFormLabel>
          <BaseFormLabel
            label="備考(その他料金の内訳など)"
            sx={{ maxWidth: "376px" }}
          >
            <Controller
              name="basicFeeMemo"
              control={control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  multiline
                  rows={4}
                  size="small"
                  value={field.value}
                  fullWidth
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </BaseFormLabel>
        </Stack>
      </AccordionDetails>
    </BaseAccordion>
  );
};
